import { OTHER_HOMELANDS } from './homelands.js';
import { MEADOW_SPOTS, WOODS_SPOTS } from './spots.js';
// Things placed in zones that both sides need to know about. The server decides what happens; the client draws them.

/** Forage spots: walk up, press E, and the server decides whether you find something. Each character has its own timer per spot. */
export interface ForageNode { id: string; map: string; tile: [number, number]; item: string; qty: [number, number]; respawnSec: number;
  /** Only there at night, or while it rains and for a while after. */
  when?: 'night' | 'rain' | 'late' }
/** Deep night (midnight to 5): when fallen stars can be found. */
export const isLate = (hour: number) => hour < 5;

export const FORAGE: ForageNode[] = [
  { id: 'town-mush-1', map: 'town', tile: [6, 10], item: 'mushroom', qty: [1, 2], respawnSec: 90 },
  { id: 'town-mush-2', map: 'town', tile: [5, 16], item: 'mushroom', qty: [1, 2], respawnSec: 90 },
  { id: 'town-herb-1', map: 'town', tile: [14, 23], item: 'herb', qty: [1, 3], respawnSec: 120 },
  { id: 'town-herb-2', map: 'town', tile: [24, 24], item: 'herb', qty: [1, 3], respawnSec: 120 },
  { id: 'town-flower-1', map: 'town', tile: [30, 9], item: 'flower', qty: [1, 1], respawnSec: 150 },
  { id: 'town-apple-1', map: 'town', tile: [37, 11], item: 'apple', qty: [1, 2], respawnSec: 120 },
  // night: moonflowers open by the flower beds and the pond
  { id: 'town-moon-1', map: 'town', tile: [33, 9], item: 'moonflower', qty: [1, 1], respawnSec: 120, when: 'night' },
  { id: 'town-moon-2', map: 'town', tile: [15, 20], item: 'moonflower', qty: [1, 1], respawnSec: 120, when: 'night' },
  { id: 'town-moon-3', map: 'town', tile: [25, 21], item: 'moonflower', qty: [1, 1], respawnSec: 120, when: 'night' },
  // deep night: fallen stars land in the Meadowlands (the Stargazer wants them)
  { id: 'meadow-star-1', map: 'meadow', tile: [12, 30], item: 'star_shard', qty: [1, 1], respawnSec: 300, when: 'late' },
  { id: 'meadow-star-2', map: 'meadow', tile: [52, 12], item: 'star_shard', qty: [1, 1], respawnSec: 300, when: 'late' },
  { id: 'meadow-star-3', map: 'meadow', tile: [44, 40], item: 'star_shard', qty: [1, 1], respawnSec: 300, when: 'late' },
  { id: 'meadow-star-4', map: 'meadow', tile: [8, 8], item: 'star_shard', qty: [1, 1], respawnSec: 300, when: 'late' },
  // rain: rain caps pop up in the shade after a shower
  { id: 'town-rain-1', map: 'town', tile: [2, 11], item: 'rain_cap', qty: [1, 2], respawnSec: 100, when: 'rain' },
  { id: 'town-rain-2', map: 'town', tile: [10, 15], item: 'rain_cap', qty: [1, 2], respawnSec: 100, when: 'rain' },
  { id: 'town-rain-3', map: 'town', tile: [37, 17], item: 'rain_cap', qty: [1, 2], respawnSec: 100, when: 'rain' },
];
// the bigger Meadowlands and Whisperwood: an orchard, a timber grove, a fishing lake, a copper quarry, herb and
// flower meadows; a woodcutters' camp, mushroom rings, ferns and a pond in the southern hollow
const spot = (map: string, kind: string, list: [number, number][], item: (i: number) => string, qty: [number, number], respawnSec: number): ForageNode[] =>
  list.map((tile, i) => ({ id: `${map}-${kind}-${i}`, map, tile, item: item(i), qty, respawnSec }));
FORAGE.push(
  ...spot('meadow', 'orchard', MEADOW_SPOTS.orchard, () => 'apple', [1, 2], 120),
  ...spot('meadow', 'grove', MEADOW_SPOTS.grove, () => 'wood', [1, 2], 120),
  ...spot('meadow', 'lake', MEADOW_SPOTS.lake, () => 'lake_trout', [1, 1], 150),
  ...spot('meadow', 'quarry', MEADOW_SPOTS.quarry, () => 'copper_ore', [1, 1], 150),
  ...spot('meadow', 'meadowherb', MEADOW_SPOTS.herbs, i => i % 2 ? 'flower' : 'herb', [1, 2], 100),
  ...spot('woods', 'camp', WOODS_SPOTS.camp, () => 'wood', [1, 2], 120),
  ...spot('woods', 'ring', WOODS_SPOTS.shrooms, () => 'mushroom', [1, 2], 100),
  ...spot('woods', 'fern', WOODS_SPOTS.ferns, () => 'fernleaf', [1, 1], 140),
  ...spot('woods', 'pond', WOODS_SPOTS.pond, () => 'lake_trout', [1, 1], 150),
);
// the other homelands' forage
for (const n of OTHER_HOMELANDS) FORAGE.push(...(n.forage ?? []));

/** How close (pixels, feet to spot centre) you must be to pick something. */
export const INTERACT_RANGE_PX = 22;
